"use client";

import { Award, ArrowRight, Star } from "lucide-react";
import { useState, useEffect } from "react";
import { cdnPath } from "../../utility/api.js";

const placedStudents = [
  {
    name: "Yugansha Mowar",
    company: "CommonWealth Bank",
    linkedinUrl: "https://www.linkedin.com/in/alice-smith",
    imgUrl: `${cdnPath}/Yugansha.jpg`,
  },
  {
    name: "Yash Kumar",
    company: "Blinkit",
    linkedinUrl: "https://www.linkedin.com/in/bob-johnson",
    imgUrl: `${cdnPath}/YashKumar.jpg`,
  },
  {
    name: "Shatakshi Chaturvedi",
    company: "BCG",
    linkedinUrl: "https://www.linkedin.com/in/charlie-brown",
    imgUrl: `${cdnPath}/Shatakshi.jpg`,
  },
  {
    name: "Yash Gaur",
    company: "GLG",
    linkedinUrl: "https://www.linkedin.com/in/diana-prince",
    imgUrl: `${cdnPath}/Yash.jpg`,
  },
];

const mentorSlides = [
  {
    img: `${cdnPath}/charchit_bcg.jpeg`,

    overlay: "bg-blue-900/50",
    text: "Strategy & transformation work at Boston Consulting Group.",
  },
  {
    img: `${cdnPath}/charchit_bain.jpeg`,

    overlay: "bg-red-900/50",
    text: "Driving results & innovation at Bain & Company.",
  },
];

const SlideShow = ({ slides }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg h-80 md:h-[360px]">
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.img}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div
            className={`absolute inset-0 ${slide.overlay} mix-blend-multiply pointer-events-none`}
          />
        </div>
      ))}
    </div>
  );
};

const PlacementHero = ({ details }) => {
  const formatDateWithOrdinal = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });

    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };

  return (
    <section className="relative w-full flex items-center justify-center overflow-hidden py-10 mt-6">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-slate-950">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.4) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-slate-950/80 via-slate-950/20 via-transparent via-slate-950/20 to-slate-950/80" />
      </div>

      <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-gray-900/20 via-gray-900/5 via-black via-gray-900/5 to-black opacity-90" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.1),transparent_50%)] md:bg-[radial-gradient(ellipse_at_left,rgba(59,130,246,0.1),transparent_50%)]" />
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-12 lg:py-16">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
          {/* Left Column */}
          <div className="w-full md:w-7/12 space-y-6 text-center md:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-950/50 border border-blue-500/30 backdrop-blur-sm">
              <div className="relative">
                <div className="absolute -inset-0.5 bg-blue-500 rounded-full animate-ping opacity-40" />
                <Award className="w-4 h-4 text-blue-400 relative" />
              </div>
              <span className="text-xs font-semibold text-blue-400 tracking-wider uppercase">
                Exclusive Off-Campus Cohort
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-3">
              <h1 className="text-3xl md:text-5xl font-bold leading-tight">
                <div className="text-white">
                  Get Placed in{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10 px-2 py-1">60 Days</span>
                    <span className="absolute inset-0 bg-yellow-400/50 transform rotate-[-2deg] rounded-sm"></span>
                  </span>{" "}
                  -
                </div>
                <div className="bg-text-gradient bg-clip-text text-transparent">
                  With Me, One on One
                </div>
              </h1>
            </div>

            {/* Description */}
            <p className="text-gray-300 leading-relaxed">
              Our 2-month placement cohort provides non-tech freshers with
              guided training and career support. . Get started as soon as you
              join.
            </p>

            {/* Simple Price Highlight */}
            <div className="inline-flex items-center gap-4 px-4 py-3 rounded-lg bg-blue-950/30 border border-blue-500/20">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-white">
                  ₹{(details?.price || 99999).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="h-6 w-px bg-blue-500/30"></div>
              <span className="text-sm text-gray-300">one-time payment</span>
            </div>

            {/* CTA Button */}
            <div className="pt-2">
              <button
                className="group relative px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px] overflow-hidden"
                onClick={() =>
                  window.open("https://forms.gle/75EvB3WvKUGFTEvw8")
                }
              >
                <div
                  className="absolute -inset-[2px] rounded-lg"
                  style={{
                    background:
                      "conic-gradient(from 0deg, #3b82f6, #8b5cf6, #3b82f6, transparent, transparent, transparent, transparent, transparent)",
                    animation: "spin 2s linear infinite",
                  }}
                ></div>
                <div className="absolute inset-[2px] bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg"></div>
                <span className="relative z-10 flex items-center gap-2 text-sm font-semibold">
                  Secure Your Spot
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 pt-4">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2 overflow-visible">
                  {placedStudents.map((student, index) => (
                    <div key={index} className="relative group">
                      <a
                        // href={student.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block h-8 w-8 rounded-full ring-1 ring-white overflow-hidden"
                      >
                        <img
                          src={student.imgUrl || "/placeholder.svg"}
                          width={32}
                          height={32}
                          alt={student.name}
                          className="object-cover h-full w-full"
                        />
                      </a>
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg shadow-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 w-max">
                        <span className="block font-semibold">
                          {student.name}
                        </span>
                        <span className="block">{student.company}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-center sm:items-start">
                  <div className="text-white text-lg font-semibold">500+</div>
                  <div className="text-gray-300 text-sm">students placed</div>
                </div>
              </div>
              <div className="hidden sm:block h-8 w-px bg-gray-700 mx-3" />
              <div className="flex-col justify-center items-center sm:items-start gap-1 sm:flex-row sm:gap-3">
                <div className="text-white text-lg font-semibold flex items-center">
                  4.6+
                  <span className="text-gray-400 text-sm ml-1">
                    (1,221 ratings)
                  </span>
                </div>
                <div className="flex gap-0.5 justify-center md:justify-start">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <div className="relative w-4 h-4">
                    <Star className="absolute top-0 left-0 w-4 h-4 text-gray-400 fill-gray-400" />
                    <Star className="absolute top-0 left-0 w-4 h-4 text-yellow-400 fill-yellow-400 clip-half" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="w-full md:w-5/12 flex flex-col mt-12 md:mt-0">
            {/* Mentor Heading */}
            <div className="mb-6 text-center md:text-left">
              <h3 className="text-white text-3xl font-extrabold tracking-tight leading-tight">
                Meet Your Mentor
              </h3>
              <div className="flex items-center justify-center md:justify-start gap-3 mt-1">
                <p className="text-[#467BFF] text-lg md:text-xl font-semibold">
                  Charchit Kurra
                </p>
                <a
                  href="https://www.linkedin.com/in/charchit-kurra-3698b821a/"
                  className="inline-flex items-center gap-2 bg-gray-900/70 backdrop-blur-sm px-2 py-1 rounded-lg shadow-lg hover:bg-gray-900/80 transition-colors"
                  aria-label="LinkedIn"
                  target="_blank"
                >
                  <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-400">
                      Bain | Founder, Off Campus
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Slideshow */}
            <SlideShow slides={mentorSlides} />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </section>
  );
};

export default PlacementHero;
